<template>
  <div
    :class="[
      'row',
      'editor-navigator-vue-header',
      { 'mx-0': !isFocusArchive },
      { 'red-banner': isFocusArchive },
      { 'justify-content-center': isFocusArchive },
      { 'sticky-top': isFocusArchive },
    ]"
  >
    <div
      v-if="!isFocusArchive"
      class="d-flex flex-row header-panel align-items-center justify-content-between"
    >
      <Breadcrumb
        v-if="isCasePathNavigatorDisplayable"
        :items="pathToDisplay"
        :isTemplate="routeType === 'template'"
        :show-house="true"
        :clickable="true"
        :ajustable-margin="40"
      ></Breadcrumb>

      <div v-else class="flex-grow-1">
        <div v-if="isResultsFilterPage" class="page-title">
          {{ $t('knowledge.search-admin.search-results') }}
        </div>
      </div>

      <div
        class="pr-2 flex-none d-flex flex-row-reverse align-items-center justify-content-between ml-auto"
      >
        <div class="options-header d-flex justify-content-end">
          <!-- SEARCH -->
          <SearchField
            v-if="isCase"
            :translate="{
              placeholder: $t('knowledge.product.search-placeholder'),
              tooltip: $t('knowledge.product.search-tooltip'),
            }"
            :updated-value="collectionSearchFilter"
            @launch-search="$emit('launch-search', $event)"
          />
          <!-- IMPORT -->
          <not-allowed-tooltip
            :user-permissions="userPermissions"
            permission-key="canCreateContent"
            placement="bottom"
          >
            <template v-slot:main-content="{ hasPermissions }">
              <div
                v-if="isCase"
                placeholder="import a word document"
                @click="hasPermissions ? (modals.importFiles = true) : ''"
              >
                <el-tooltip
                  :disabled="!hasPermissions"
                  :content="$t('knowledge.actions.import-files')"
                >
                  <span class="import-files__icon-wrapper">
                    <font-awesome-icon
                      class="import-files__icon"
                      :icon="['fal', 'file-import']"
                    />
                  </span>
                </el-tooltip>
              </div>
            </template>
          </not-allowed-tooltip>
          <!-- ELLIPSIS DROPDOWN -->
          <CaseHeaderOptions
            v-if="isCase || isContent || isTemplate"
            :options="headerOptions"
            :user-permissions="userPermissions"
            @open-delete-modal="$emit('open-delete-modal', $event)"
            @open-clone-modal="$emit('open-clone-modal', $event)"
            @open-content-version="$emit('open-content-version', $event)"
            @open-content-settings="$emit('open-content-settings', $event)"
            @open-integration-modal="$emit('open-integration-modal')"
            @archive="$emit('archive', $event)"
          />
        </div>
      </div>
    </div>
    <ArchiveViewerHeader v-else />
    <ImportFiles
      :is-loading="importFilesIsLoading"
      v-if="modals.importFiles"
      :display="modals.importFiles"
      @importFiles="importFiles"
      @close="modals.importFiles = false"
    />
  </div>
</template>

<script>
import SearchField from '@/components/SearchField';
import CaseHeaderOptions from '@/components/Commons/CasesNavigator/CaseHeaderOptions';
import ArchiveViewerHeader from './ArchiveViewerHeader';
import ImportFiles from '@/components/Modals/ImportFiles';
import NotAllowedTooltip from '@/components/Commons/NotAllowedTooltip';
import Breadcrumb from '../../../components/SuperSearch/Breadcrumb.vue';
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'ParametricKnowledgeEditorHeader',
  components: {
    SearchField,
    CaseHeaderOptions,
    ArchiveViewerHeader,
    ImportFiles,
    NotAllowedTooltip,
    Breadcrumb,
  },
  props: {
    casePath: {
      type: Array,
      default: () => [],
    },
    routeType: {
      type: String,
    },
    collectionSearchFilter: {
      type: String,
      default: '',
    },
    isSettingsDrawerOpen: {
      type: Boolean,
    },
  },
  data() {
    return {
      importFilesIsLoading: false,
      modals: {
        importFiles: false,
      },
    };
  },
  computed: {
    isFocusArchive() {
      return [
        'knowledge-archive',
        'knowledge-case-archive',
        'knowledge-case-archive-content',
      ].includes(this.$route.name);
    },
    pathToDisplay() {
      return this.isTemplate ? this.templatesPath : this.casePath;
    },
    isCasePathNavigatorDisplayable() {
      return [
        'web-content',
        'web-content-step',
        'knowledge-templates',
        'knowledge-template',
        'knowledge-case',
        'knowledge-content',
        'knowledge-content-step',
      ].includes(this.$route.name);
    },
    isResultsFilterPage() {
      const path = this.$route.path.split('/');
      return path[2] === 'filter';
    },
    isCase() {
      return this.$route.path.split('/').includes('case');
    },
    isContent() {
      return this.$route.path.split('/').includes('content');
    },
    isArticle() {
      return (
        this.isContent &&
        !!this.focusContent &&
        this.focusContent.type === 'Article'
      );
    },
    isTemplate() {
      return this.$route.path.includes('templates');
    },
    hasImportedIntegration() {
      return this.hasZendesk || this.hasNotion || this.hasSalesforce;
    },
    headerOptions() {
      return {
        contentSettings: this.isContent,
        copyToClipboard: !this.isParametric,
        deleteCase: this.isCase,
        cloneCase:
          this.isCase &&
          !this.focusKnowledgeIsMultilinguale &&
          !this.isParametric,
        deleteContent: this.isContent,
        versionContent: this.isContent,
        integrationImport: this.isArticle && this.hasImportedIntegration,
        settings: this.isContent,
        createTemplate: this.isContent && !this.isParametric,
        isSettingsDrawerOpen: this.isSettingsDrawerOpen,
      };
    },
    userPermissions() {
      if (this.isCase) {
        return this.focusCollection ? this.focusCollection.userPermissions : {};
      } else {
        return this.focusContent ? this.focusContent.userPermissions : {};
      }
    },
    ...mapGetters(['hasZendesk', 'hasNotion', 'hasSalesforce', 'isParametric']),
    ...mapGetters('knowledgeModule', [
      'focusContent',
      'focusCollection',
      'focusKnowledgeIsMultilinguale',
      'templatesPath',
      'editingLanguage',
    ]),
  },
  methods: {
    async importFiles(files) {
      this.modals.importFiles == false;
      const currentCasePath = this.casePath;
      const caseId = currentCasePath.slice(-1)[0].id;
      if (files.length) {
        const formData = new FormData();

        for (let i = 0; i < files.length; i++) {
          let file = files[i];
          formData.append('files', file);
        }
        formData.append('caseId', caseId);
        try {
          this.importFilesIsLoading = true;
          const res = await this.$services.parametricContents.importFiles(
            formData,
          );

          this.importFilesIsLoading = false;
          this.modals.importFiles = false;
          const contents = res.data.createdContents.map((c) => {
            const language = c.translations[this.editingLanguage]
              ? this.editingLanguage
              : c.defaultLanguage;
            const translations = c.translations[language];
            return {
              id: c._id,
              label: translations.label,
              body: translations.body,
              returnLanguage: language,
              setLanguages: [language],
              ...c,
            };
          });
          this.$message({
            message: this.$tc(
              'knowledge.article-builder.side-bar.imported-docx.notification.content',
              contents.length,
            ),
            type: 'success',
          });
          this.insertImportedFilesInCollection(contents);
          return contents;
        } catch (err) {
          this.$message({
            message: this.$tc(
              'knowledge.article-builder.side-bar.imported-docx.notification.error-title',
              this.files.length,
            ),
            type: 'error',
          });
          this.importFilesIsLoading = false;
          this.modals.importFiles = false;
          return err;
        }
      } else {
        return 'pas de fichier';
      }
    },
    ...mapActions('knowledgeModule', ['insertImportedFilesInCollection']),
  },
};
</script>

<style lang="scss" scoped>
.editor-navigator-vue-header {
  background-color: white;
  height: 60px;
}

.header-panel {
  width: 100%;
}

.page-title {
  font-size: 12px;
  color: $grey-6-mayday;
  font-weight: 600;
}

.red-banner {
  background-color: #f5365c !important;
  padding-left: 0;
  padding-right: 0;
}

.breadcrumb-overflow {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-all;
}

.options-header {
  div {
    margin: 0 4px;
  }
}
.import-files__icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  color: $grey-7-mayday;
  width: 24px;
  height: 24px;
  &:hover {
    background-color: $grey-2-mayday;
  }
  .import-files__icon {
    font-size: 16px;
    width: 16px;
    height: 16px;
  }
}

.flex-none {
  flex: none;
}
</style>
